/** Una lectura de la serie de una combinación lote × plaga. */
export type LecturaProyeccion = {
  /** Fecha de la sesión (`yyyy-MM-dd`). */
  fecha: string;
  /** Incidencia medida: porcentaje de árboles afectados. */
  valor: number;
};

/** Estimación de cuánto falta para que una plaga cruce su umbral de alerta. */
export type Proyeccion = {
  /** Días estimados desde la última lectura hasta cruzar el umbral de alerta. */
  diasHastaUmbral: number;
  /** El plazo ya redactado para mostrar ("2 semanas", "6 días"). */
  plazo: string;
  /** Qué tan bien la recta describe la serie (0–1). */
  r2: number;
};

/** Mínimo de lecturas para intentar un ajuste. Con tres puntos cualquier recta
 *  parece buena; con cuatro empieza a significar algo. */
const MIN_LECTURAS = 4;

/** Cuántas lecturas recientes entran al ajuste. La presión de plagas cambia de
 *  régimen (lluvias, controles, brotación), así que el pasado lejano estorba
 *  más de lo que ayuda para predecir la próxima semana. */
const MAX_LECTURAS = 6;

/** Ajuste mínimo aceptable. Por debajo de esto la recta no describe la serie y
 *  la pendiente es ruido: mejor no decir nada que dar un plazo inventado. */
const MIN_R2 = 0.5;

/** Horizonte máximo que se reporta. Más allá de ~2 meses la extrapolación deja
 *  de ser información y pasa a ser adivinanza. */
const HORIZONTE_MAX_DIAS = 56;

/** Hasta cuántos días se expresa el plazo en días; de ahí en más, en semanas. */
const LIMITE_EN_DIAS = 10;

/**
 * Estima en cuánto tiempo una combinación lote × plaga cruzaría su umbral de
 * alerta, si mantuviera el ritmo de las últimas lecturas.
 *
 * Ajusta una recta por mínimos cuadrados **contra la fecha, no contra el número
 * de sesión**: las sesiones no están espaciadas de forma regular (puede haber
 * varias en una misma semana), y tratarlas como pasos equivalentes infla la
 * pendiente cuando se monitorea seguido.
 *
 * Devuelve `null` —o sea, no se muestra nada— en todos estos casos:
 *
 * - **Plaga crítica o sin umbral**: no hay gradiente que proyectar, cualquier
 *   detección ya es alerta.
 * - **Pocas lecturas** (menos de `MIN_LECTURAS`).
 * - **Ya cruzó el umbral**: anunciar cuándo lo cruzará no aporta nada.
 * - **No sube** (pendiente ≤ 0).
 * - **Mal ajuste** (`r2 < MIN_R2`): la serie no se parece a una recta.
 * - **Plazo fuera del horizonte** o ya vencido.
 *
 * ⚠️ Lo que este cálculo NO sabe: si hubo una **aplicación** entre dos
 * sesiones, la pendiente anterior no dice nada del futuro y el plazo va a
 * anunciar una subida que el control ya evitó. Cruzarlo con el módulo de
 * aplicaciones es la mejora natural, y desde el 2026-10-15 **ya es posible**:
 * `AplicacionResponse.lotes[]` trae el `loteId` de cada lote aplicado, así que
 * asociar la aplicación al lote dejó de exigir intersectar el recorrido con el
 * polígono. Sigue sin estar hecho, y con un límite: esos lotes **solo existen en
 * las aplicaciones manuales**; una por dron sigue siendo nada más que una
 * trayectoria.
 */
export function calcularProyeccion(
  lecturas: LecturaProyeccion[],
  umbralAlerta: number | null,
): Proyeccion | null {
  // Recibe el umbral y no la plaga del catálogo: así no le importa de dónde
  // salga —referencia congelada hoy, tabla por cultivo cuando exista `6.2`— y
  // queda una función pura, que se puede probar sin armar un item de catálogo.
  //
  // `null` es "no hay proyección posible": organismo crítico (no tiene
  // gradiente, cualquier detección ya es alerta) o sin umbral de alerta
  // definido, y sin ese número no hay nada que cruzar.
  if (umbralAlerta === null || umbralAlerta <= 0) return null;

  const recientes = lecturas.slice(-MAX_LECTURAS);
  if (recientes.length < MIN_LECTURAS) return null;

  const ultima = recientes[recientes.length - 1];
  if (ultima.valor >= umbralAlerta) return null;

  // Días relativos a la primera lectura del tramo: solo importan las
  // diferencias, no la fecha absoluta.
  const origen = _aDias(recientes[0].fecha);
  const puntos = recientes.map((lectura) => ({
    dias: _aDias(lectura.fecha) - origen,
    valor: lectura.valor,
  }));

  const recta = _ajustarRecta(puntos);
  if (!recta || recta.pendiente <= 0 || recta.r2 < MIN_R2) return null;

  // Día en que la recta alcanza el umbral, medido desde la última lectura.
  const diaDelCruce = (umbralAlerta - recta.ordenada) / recta.pendiente;
  const diasHastaUmbral = diaDelCruce - puntos[puntos.length - 1].dias;
  if (diasHastaUmbral <= 0 || diasHastaUmbral > HORIZONTE_MAX_DIAS) return null;

  return {
    diasHastaUmbral,
    plazo: _formatPlazo(diasHastaUmbral),
    r2: recta.r2,
  };
}

/** Punto de la serie: días transcurridos y valor medido. */
type Punto = { dias: number; valor: number };

/** Recta ajustada más su bondad de ajuste. */
type Recta = { pendiente: number; ordenada: number; r2: number };

/** Ajuste por mínimos cuadrados. Devuelve `null` si todas las lecturas caen el
 *  mismo día (no hay eje X sobre el que ajustar). */
function _ajustarRecta(puntos: Punto[]): Recta | null {
  const n = puntos.length;
  let sumaX = 0;
  let sumaY = 0;
  let sumaXY = 0;
  let sumaXX = 0;
  for (const punto of puntos) {
    sumaX += punto.dias;
    sumaY += punto.valor;
    sumaXY += punto.dias * punto.valor;
    sumaXX += punto.dias * punto.dias;
  }

  const denominador = n * sumaXX - sumaX * sumaX;
  if (denominador === 0) return null;

  const pendiente = (n * sumaXY - sumaX * sumaY) / denominador;
  const ordenada = (sumaY - pendiente * sumaX) / n;

  // R²: cuánta de la variación de la serie explica la recta.
  const mediaY = sumaY / n;
  let residual = 0;
  let total = 0;
  for (const punto of puntos) {
    const estimado = ordenada + pendiente * punto.dias;
    residual += (punto.valor - estimado) ** 2;
    total += (punto.valor - mediaY) ** 2;
  }
  // Serie plana: no hay variación que explicar. La rechaza igual el filtro de
  // pendiente, pero devolver 0 evita una división por cero.
  const r2 = total === 0 ? 0 : 1 - residual / total;

  return { pendiente, ordenada, r2 };
}

/** Número de día absoluto de una fecha `yyyy-MM-dd`. Se construye en UTC para
 *  que el huso horario no corra la fecha un día. */
function _aDias(fecha: string): number {
  const [anio, mes, dia] = fecha.slice(0, 10).split("-").map(Number);
  return Date.UTC(anio, mes - 1, dia) / 86_400_000;
}

/** Redacta el plazo. En días cuando es inminente —"3 días" se entiende mejor
 *  que "0 semanas"— y en semanas cuando es más lejano, que es la unidad en la
 *  que se planifica un monitoreo. */
function _formatPlazo(dias: number): string {
  const enDias = Math.max(1, Math.round(dias));
  if (enDias <= LIMITE_EN_DIAS) {
    return `${enDias} ${enDias === 1 ? "día" : "días"}`;
  }
  const semanas = Math.max(1, Math.round(enDias / 7));
  return `${semanas} ${semanas === 1 ? "semana" : "semanas"}`;
}
